import React, { useState, useEffect } from 'react';
import { getAllLoads, getLoadResponses, selectCarrierForLoad } from '../../ApiController/ApiService';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export default function LoadsContainer() {
  const { t } = useTranslation();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadType, setLoadType] = useState(''); // 'LTL' or 'FTL'

  // Responses Modal State
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [responses, setResponses] = useState([]);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [selectingCarrierId, setSelectingCarrierId] = useState(null);

  // Details Modal State
  const [detailsLoad, setDetailsLoad] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleViewDetails = (load) => {
    setDetailsLoad(load);
    setShowDetailsModal(true);
  };

  const handleViewResponses = async (load) => {
    setSelectedLoad(load);
    setShowResponsesModal(true);
    setLoadingResponses(true);
    try {
      const res = await getLoadResponses(load._id);
      setResponses(res.responses || res.data || res || []);
    } catch (err) {
      toast.error(err.message || "Failed to fetch responses");
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleSelectCarrier = async (carrierId) => {
    try {
      setSelectingCarrierId(carrierId);
      await selectCarrierForLoad(selectedLoad._id, { carrierId });
      toast.success("Carrier selected successfully!");
      setShowResponsesModal(false);
      fetchLoads();
    } catch (err) {
      toast.error(err.message || "Failed to select carrier");
    } finally {
      setSelectingCarrierId(null);
    }
  };

  const fetchLoads = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10 };
      if (loadType) params.loadType = loadType;

      const data = await getAllLoads(params);
      // Depending on API response structure
      const docs = data?.docs || data?.data?.docs || [];
      const total = data?.totalPages || data?.data?.totalPages || 1;
      
      setLoads(docs);
      setTotalPages(total);
    } catch (err) {
      if (err?.response?.status === 404 || err?.message?.includes('404')) {
        setLoads([]);
        setTotalPages(1);
      } else {
        toast.error(err.message || 'Failed to fetch loads');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoads();
  }, [currentPage, loadType]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 text-white font-sans bg-[#0F0A2B] min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold">{t('loads.title')}</h1>
        <div className="flex bg-[#151b40] p-1 border border-[#2A2A4D] rounded-full">
          <button
            onClick={() => { setLoadType(''); setCurrentPage(1); }}
            className={`py-1.5 px-4 rounded-full text-sm font-semibold transition ${!loadType ? 'bg-white text-[#003057]' : 'text-[#6E7A90] hover:text-white'}`}
          >
            {t('loads.all')}
          </button>
          <button
            onClick={() => { setLoadType('LTL'); setCurrentPage(1); }}
            className={`py-1.5 px-4 rounded-full text-sm font-semibold transition ${loadType === 'LTL' ? 'bg-white text-[#003057]' : 'text-[#6E7A90] hover:text-white'}`}
          >
            {t('loads.ltl')}
          </button>
          <button
            onClick={() => { setLoadType('FTL'); setCurrentPage(1); }}
            className={`py-1.5 px-4 rounded-full text-sm font-semibold transition ${loadType === 'FTL' ? 'bg-white text-[#003057]' : 'text-[#6E7A90] hover:text-white'}`}
          >
            {t('loads.ftl')}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-[#1A1F45] rounded-xl shadow-lg border border-[#2A2A4D]">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-300 uppercase bg-[#0F0A2B] border-b border-[#2A2A4D]">
            <tr>
              <th className="px-6 py-4">{t('loads.type')}</th>
              <th className="px-6 py-4">{t('loads.origin')}</th>
              <th className="px-6 py-4">{t('loads.destination')}</th>
              <th className="px-6 py-4">{t('loads.dateTime')}</th>
              <th className="px-6 py-4">{t('loads.price')}</th>
              <th className="px-6 py-4">{t('loads.status')}</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-8">{t('loads.loading')}</td></tr>
            ) : loads.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8">{t('loads.noLoadsFound')}</td></tr>
            ) : (
              loads.map((load, i) => (
                <tr key={load._id || i} className="border-b border-[#2A2A4D] hover:bg-[#202550] transition">
                  <td className="px-6 py-4 font-semibold text-cyan-400">
                    {(load.loadType || load.shipmentType || loadType || 'LTL').toUpperCase() === 'FTL' ? t('loads.ftl') : t('loads.ltl')}
                  </td>
                  <td className="px-6 py-4 truncate max-w-[200px]" title={load.pickupAddress}>{load.pickupAddress || 'N/A'}</td>
                  <td className="px-6 py-4 truncate max-w-[200px]" title={load.deliveryAddress}>{load.deliveryAddress || 'N/A'}</td>
                  <td className="px-6 py-4">{load.pickupDate} {load.pickupTime}</td>
                  <td className="px-6 py-4 text-green-400">${load.deliveryPrice}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      load.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-500' :
                      load.status === 'Assigned' ? 'bg-blue-500/20 text-blue-500' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {load.status || 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 flex items-center gap-2">
                    <button 
                      onClick={() => handleViewDetails(load)}
                      className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-3 py-1.5 rounded text-sm transition"
                    >
                      Details
                    </button>
                    <button 
                      onClick={() => handleViewResponses(load)}
                      className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white px-3 py-1.5 rounded text-sm transition"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="flex justify-between items-center bg-[#151b40] p-4 rounded-xl border border-[#2A2A4D]">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          className="px-4 py-2 bg-[#4100B4] rounded-lg disabled:opacity-50 transition hover:bg-[#5926E7]"
        >
          {t('loads.previous')}
        </button>
        <span className="text-sm">{t('loads.page', { current: currentPage, total: totalPages })}</span>
        <button
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
          className="px-4 py-2 bg-[#4100B4] rounded-lg disabled:opacity-50 transition hover:bg-[#5926E7]"
        >
          {t('loads.next')}
        </button>
      </div>


      {/* Details Modal */}
      {showDetailsModal && detailsLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-[#1A1F45] border border-[#2A2A4D] rounded-2xl w-full max-w-2xl p-0 relative max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="p-6 border-b border-[#2A2A4D] flex justify-between items-center bg-[#0F0A2B]/80">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">Load Details</h3>
                <p className="text-sm text-gray-400 mt-1">Detailed view of shipment information</p>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2A2A4D]/50 text-gray-400 hover:text-white hover:bg-red-500/80 transition">✕</button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-[#151b40] space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase">Load ID</p>
                  <p className="font-semibold text-white">{detailsLoad._id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Status</p>
                  <p className="font-semibold text-white">{detailsLoad.status}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-400 uppercase">Pickup Address</p>
                  <p className="font-semibold text-white">{detailsLoad.pickupAddress}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-400 uppercase">Delivery Address</p>
                  <p className="font-semibold text-white">{detailsLoad.deliveryAddress}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Delivery Date</p>
                  <p className="font-semibold text-white">{detailsLoad.deliveryDate || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Item Type</p>
                  <p className="font-semibold text-white">{detailsLoad.shippingItemType || detailsLoad.shipmentType || '-'}</p>
                </div>
              </div>

              {/* Package Details */}
              {detailsLoad.packageDetails ? (
                <div className="bg-[#0F0A2B] p-4 rounded-xl border border-[#2A2A4D]">
                  <h4 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Package Details</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Boxes</p>
                      <p className="font-semibold text-white">{detailsLoad.packageDetails.numberOfBoxes || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Weight Per Box</p>
                      <p className="font-semibold text-white">{detailsLoad.packageDetails.weightPerBox || '-'} lbs</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Total Weight</p>
                      <p className="font-semibold text-white">{detailsLoad.packageDetails.totalWeight || '-'} lbs</p>
                    </div>
                    {detailsLoad.packageDetails.dimensions && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-400 uppercase">Dimensions (in)</p>
                        <p className="font-semibold text-white">
                          {detailsLoad.packageDetails.dimensions.length} L x {detailsLoad.packageDetails.dimensions.width} W x {detailsLoad.packageDetails.dimensions.height} H
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-[#0F0A2B] p-4 rounded-xl border border-[#2A2A4D]">
                  <h4 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Freight Details</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Commodity</p>
                      <p className="font-semibold text-white">{detailsLoad.commodity || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Weight</p>
                      <p className="font-semibold text-white">{detailsLoad.weight || '-'} lbs</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Pallets</p>
                      <p className="font-semibold text-white">{detailsLoad.pallets || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Pieces</p>
                      <p className="font-semibold text-white">{detailsLoad.pieces || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Details */}
              {(detailsLoad.additionalDetails || detailsLoad.liftGateRequired) && (
                <div className="bg-[#0F0A2B] p-4 rounded-xl border border-[#2A2A4D]">
                  <h4 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Additional Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Load Category</p>
                      <p className="font-semibold text-white">{detailsLoad.additionalDetails?.loadCategory || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Liftgate</p>
                      <p className="font-semibold text-white">
                        {detailsLoad.additionalDetails?.needsLiftgate || detailsLoad.liftGateRequired ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400 uppercase">Special Instructions</p>
                      <p className="font-semibold text-white">{detailsLoad.additionalDetails?.specialInstructions || detailsLoad.specialInstructions || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Responses Modal */}
      {showResponsesModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-[#1A1F45] border border-[#2A2A4D] rounded-2xl w-full max-w-3xl p-0 relative max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-[#2A2A4D] flex justify-between items-center bg-[#0F0A2B]/80">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  Manage Load Responses
                </h3>
                <p className="text-sm text-gray-400 mt-1">Review carrier bids and select the best option</p>
              </div>
              <button 
                onClick={() => setShowResponsesModal(false)} 
                className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2A2A4D]/50 text-gray-400 hover:text-white hover:bg-red-500/80 transition-all duration-200"
              >
                ✕
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-[#151b40]">
              {selectedLoad.selectedCarrierId ? (
                <div className="mb-6 p-4 bg-[#7c3aed]/10 border border-[#7c3aed]/30 rounded-xl flex items-start gap-3">
                  <div className="mt-0.5 text-[#7c3aed]">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#7c3aed]">Load Assigned</h4>
                    <p className="text-sm text-[#7c3aed]/80 mt-1">This load is already assigned to Carrier ID: {selectedLoad.selectedCarrierId}. Current Status: <span className="font-bold text-white">{selectedLoad.status}</span></p>
                  </div>
                </div>
              ) : null}

              {loadingResponses ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <svg className="animate-spin h-10 w-10 text-[#7c3aed] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-400 text-lg font-medium">Loading responses...</p>
                </div>
              ) : responses.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-[#2A2A4D]/30 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-[#6E7A90]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>
                  </div>
                  <p className="text-gray-400 text-lg">No responses yet for this load.</p>
                  <p className="text-[#6E7A90] text-sm mt-1">Check back later when carriers place bids.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {responses.map((resp, idx) => (
                    <div key={idx} className="group bg-[#0F0A2B] hover:bg-[#1A1F45] p-5 rounded-xl border border-[#2A2A4D] hover:border-[#7c3aed]/50 transition-all duration-300 shadow-md">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        
                        {/* Carrier Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-lg font-bold text-white">{resp.carrierId?.email || resp.carrierId?.companyName || resp.carrierId?._id || 'Unknown Carrier'}</h4>
                            <span className="px-2.5 py-0.5 bg-[#4100B4]/30 text-[#8b5cf6] rounded-full text-xs font-semibold border border-[#4100B4]/50">
                              {resp.response || 'Interested'}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-[#6E7A90] mb-4">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                            <span>Pickup: <span className="text-gray-300">{resp.estimatedPickupTime ? new Date(resp.estimatedPickupTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}</span></span>
                          </div>

                          {/* Compliance Tags */}
                          {resp.carrierCompliance && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#1A1F45] border border-[#2A2A4D] text-xs">
                                <span className="text-gray-400">Insurance:</span> 
                                <span className={`font-semibold ${resp.carrierCompliance.insuranceVerificationStatus === 'Approved' ? 'text-green-400' : 'text-yellow-400'}`}>
                                  {resp.carrierCompliance.insuranceVerificationStatus || 'Pending'}
                                </span>
                              </div>
                              {resp.carrierCompliance.insuranceExpiryDate && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#1A1F45] border border-[#2A2A4D] text-xs">
                                  <span className="text-gray-400">Expires:</span> 
                                  <span className="text-gray-200">{new Date(resp.carrierCompliance.insuranceExpiryDate).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Price & Action */}
                        <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-3 border-t md:border-t-0 md:border-l border-[#2A2A4D] pt-4 md:pt-0 md:pl-6">
                          <div className="text-left md:text-right">
                            <p className="text-xs text-[#6E7A90] uppercase tracking-wider font-semibold">Proposed Price</p>
                            <p className="text-2xl font-black text-[#10b981]">${resp.proposedPrice}</p>
                          </div>
                          
                          {!selectedLoad.selectedCarrierId && (
                            <button 
                              onClick={() => handleSelectCarrier(resp.carrierId?._id || resp.carrierId)}
                              disabled={selectingCarrierId === (resp.carrierId?._id || resp.carrierId)}
                              className="bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-bold transition-all shadow-[0_0_10px_rgba(124,58,237,0.3)] hover:shadow-[0_0_15px_rgba(124,58,237,0.5)] transform hover:-translate-y-0.5 flex items-center gap-2 justify-center min-w-[140px]"
                            >
                              {selectingCarrierId === (resp.carrierId?._id || resp.carrierId) ? (
                                <>
                                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Selecting...
                                </>
                              ) : (
                                'Select Carrier'
                              )}
                            </button>
                          )}
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
}
